import React, { useEffect, useRef } from 'react';
import styled from 'styled-components';
import { DayPicker } from 'components/molecules/DayPicker';
import { DayPickerProps, Mode } from 'react-day-picker';
import { useTranslation } from 'react-i18next';
import { Flex } from 'components/common/styled/common-styled';
import { PrimaryButton, SecondaryButton } from 'components/theme/Button';
import { Popover } from 'components/atoms/Popover';
import { PopoverContentProps } from '@radix-ui/react-popover';

type DayPickerPropsWithMode = DayPickerProps & {
  mode: Mode;
  isLoading?: boolean;
  classNamePrefix?: string;
};

type FooterProps = {
  onClearButtonClick?: React.MouseEventHandler;
  onCancelButtonClick?: React.MouseEventHandler<HTMLButtonElement>;
  onApplyButtonClick?: React.MouseEventHandler<HTMLButtonElement>;
  isClearButtonDisabled?: boolean;
  isApplyButtonDisabled?: boolean;
  isCancelButtonDisabled?: boolean;
  onApplyButtonKeyDown?: (e: React.KeyboardEvent<HTMLButtonElement>) => void;
};

type PopoverProps = {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  asChild?: boolean;
  trigger?: React.ReactNode;
  triggerRef?: React.RefObject<HTMLElement>;
  align?: PopoverContentProps['align'];
  side?: PopoverContentProps['side'];
  sideOffset?: PopoverContentProps['sideOffset'];
  alignOffset?: PopoverContentProps['alignOffset'];
  portalled?: boolean;
};

type DayPickerPopoverType = DayPickerPropsWithMode & FooterProps & PopoverProps;

const DayPickerPopover = ({
  open,
  onOpenChange,
  asChild,
  trigger,
  triggerRef,
  align = 'start',
  side = 'bottom',
  sideOffset,
  alignOffset,
  portalled = true,
  onClearButtonClick,
  isClearButtonDisabled,
  onCancelButtonClick,
  isCancelButtonDisabled,
  onApplyButtonClick,
  isApplyButtonDisabled,
  onApplyButtonKeyDown,
  classNamePrefix,
  ...props
}: DayPickerPopoverType) => {
  const { t: translate } = useTranslation();
  const previousOpenStateRef = useRef<boolean>(false);

  // Return focus to trigger element when popover closes
  useEffect(() => {
    if (!open && previousOpenStateRef.current) {
      // Popover just closed - return focus to trigger element
      // Use setTimeout to ensure the popover close transition completes
      setTimeout(() => {
        if (triggerRef?.current) {
          triggerRef.current.focus();
        }
      }, 10);
    }
    previousOpenStateRef.current = open ?? false;
  }, [open, triggerRef]);

  return (
    <Popover
      open={open}
      onOpenChange={onOpenChange}
      trigger={trigger}
      asChild={asChild}
      align={align}
      side={side}
      portalled={portalled}
      sideOffset={sideOffset}
      alignOffset={alignOffset}
      aria-modal
      aria-label={translate('selectDate')}
    >
      <DayPicker
        classNamePrefix={classNamePrefix}
        footer={
          <StyledFooter
            direction="row"
            justifyContent="space-between"
            gap={12}
            role="group"
            width="100%"
          >
            {!!onClearButtonClick && (
              <ClearButton
                className="date-range-calendar-dropdown-clear-picker-button"
                onClick={onClearButtonClick}
                disabled={isClearButtonDisabled}
                tabIndex={0}
                aria-label={translate('clear')}
              >
                {translate('clear')}
              </ClearButton>
            )}
            <Flex
              direction="row"
              width="100%"
              justifyContent="flex-end"
              gap={12}
              role="group"
            >
              <SecondaryButton
                className="date-range-calendar-cancel-picker-button"
                onClick={onCancelButtonClick}
                disabled={isCancelButtonDisabled}
                tabIndex={0}
                aria-label={translate('cancel')}
              >
                {translate('cancel')}
              </SecondaryButton>
              <PrimaryButton
                aria-disabled={isApplyButtonDisabled ?? false}
                aria-label={translate('apply')}
                className="calendar-apply-button"
                tabIndex={0}
                onClick={(e) => {
                  if (isApplyButtonDisabled) {
                    e.preventDefault();
                    return;
                  }
                  onApplyButtonClick?.(e);
                }}
                onKeyDown={(e) => {
                  if (isApplyButtonDisabled) {
                    e.preventDefault();
                    return;
                  }
                  onApplyButtonKeyDown?.(e);
                }}
              >
                {translate('apply')}
              </PrimaryButton>
            </Flex>
          </StyledFooter>
        }
        {...props}
      />
    </Popover>
  );
};

const StyledFooter = styled(Flex)`
  margin-top: 16px;
`;

const ClearButton = styled(SecondaryButton)`
  border: none;
  text-decoration: underline;

  &:hover {
    border: none;
  }
`;

export default DayPickerPopover;
